import React, { useState, useEffect } from 'react';
import { useQuery, useAction } from 'wasp/client/operations';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { ChevronDown, Check, X, DollarSign, Copy } from 'lucide-react';

interface WaitlistEntry {
  id: string;
  email: string;
  name?: string;
  position?: number;
  status: string;
  paymentStatus: string;
  lemonsqueezyCustomerId?: string;
  notes?: string;
  approvedAt?: string;
  paidAt?: string;
  createdAt: string;
}

interface Stats {
  total: number;
  pending: number;
  approved: number;
  paid: number;
  rejected: number;
}

export default function AdminBetaWaitlistPage() {
  const [filter, setFilter] = useState('pending');
  const [entries, setEntries] = useState<WaitlistEntry[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedEntry, setSelectedEntry] = useState<string | null>(null);
  const [expandedEntry, setExpandedEntry] = useState<string | null>(null);

  // Placeholder actions - replace with actual queries
  const fetchEntries = async () => {
    try {
      // This will be populated from the actual query
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch entries:', error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEntries();
  }, [filter]);

  const statusColors: Record<string, string> = {
    pending: 'bg-yellow-100 text-yellow-900',
    approved: 'bg-blue-100 text-blue-900',
    rejected: 'bg-red-100 text-red-900',
  };

  const paymentColors: Record<string, string> = {
    unpaid: 'bg-gray-100 text-gray-900',
    paid: 'bg-green-100 text-green-900',
    refunded: 'bg-purple-100 text-purple-900',
  };

  return (
    <div className="min-h-screen bg-sand p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-dusk mb-2">Beta Waitlist Management</h1>
          <p className="text-supporting">Manage founding member applications and approvals</p>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            <Card className="bg-white">
              <CardContent className="p-4">
                <div className="text-3xl font-bold text-dusk">{stats.total}</div>
                <div className="text-sm text-supporting">Total Signups</div>
              </CardContent>
            </Card>
            <Card className="bg-yellow-50 border-yellow-200">
              <CardContent className="p-4">
                <div className="text-3xl font-bold text-yellow-900">{stats.pending}</div>
                <div className="text-sm text-yellow-800">Pending</div>
              </CardContent>
            </Card>
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-4">
                <div className="text-3xl font-bold text-blue-900">{stats.approved}</div>
                <div className="text-sm text-blue-800">Approved</div>
              </CardContent>
            </Card>
            <Card className="bg-green-50 border-green-200">
              <CardContent className="p-4">
                <div className="text-3xl font-bold text-green-900">{stats.paid}</div>
                <div className="text-sm text-green-800">Paid ✓</div>
              </CardContent>
            </Card>
            <Card className="bg-red-50 border-red-200">
              <CardContent className="p-4">
                <div className="text-3xl font-bold text-red-900">{stats.rejected}</div>
                <div className="text-sm text-red-800">Rejected</div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filter Buttons */}
        <div className="flex gap-2 mb-6 flex-wrap">
          {['pending', 'approved', 'paid', 'all'].map((f) => (
            <Button
              key={f}
              onClick={() => setFilter(f)}
              variant={filter === f ? 'default' : 'secondary'}
              className={
                filter === f
                  ? 'bg-coral text-ink'
                  : 'bg-white border border-border/30 text-dusk desktop-hover:bg-gray-100'
              }
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </Button>
          ))}
        </div>

        {/* Waitlist Table */}
        <Card className="bg-white">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-border/20">
                  <tr>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-dusk">Email</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-dusk">Position</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-dusk">Status</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-dusk">Payment</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-dusk">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-6 py-8 text-center text-supporting">
                        No entries found
                      </td>
                    </tr>
                  ) : (
                    entries.map((entry) => (
                      <React.Fragment key={entry.id}>
                        <tr className="border-b border-border/10 hover:bg-gray-50 transition-colors">
                          <td className="px-6 py-4">
                            <div>
                              <div className="font-medium text-dusk">{entry.email}</div>
                              {entry.name && (
                                <div className="text-sm text-supporting">{entry.name}</div>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm text-dusk">
                            {entry.position ? `#${entry.position}` : '-'}
                          </td>
                          <td className="px-6 py-4">
                            <Badge className={statusColors[entry.status] || ''}>
                              {entry.status}
                            </Badge>
                          </td>
                          <td className="px-6 py-4">
                            <Badge className={paymentColors[entry.paymentStatus] || ''}>
                              {entry.paymentStatus}
                            </Badge>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex gap-2">
                              <Button
                                variant="secondary"
                                size="sm"
                                onClick={() =>
                                  setExpandedEntry(
                                    expandedEntry === entry.id ? null : entry.id
                                  )
                                }
                              >
                                <ChevronDown className="w-4 h-4" />
                              </Button>
                              {entry.paymentStatus === 'unpaid' && (
                                <Button
                                  variant="secondary"
                                  size="sm"
                                  className="bg-green-100 text-green-900 hover:bg-green-200"
                                >
                                  <DollarSign className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                        {expandedEntry === entry.id && (
                          <tr className="bg-gray-50">
                            <td colSpan={5} className="px-6 py-4">
                              <div className="space-y-3">
                                {entry.notes && (
                                  <div>
                                    <div className="text-sm font-semibold text-dusk mb-1">
                                      Notes
                                    </div>
                                    <div className="text-sm text-supporting">{entry.notes}</div>
                                  </div>
                                )}
                                <div className="flex gap-3">
                                  {entry.status === 'pending' && (
                                    <>
                                      <Button
                                        size="sm"
                                        className="bg-green-600 text-white hover:bg-green-700"
                                      >
                                        <Check className="w-4 h-4 mr-1" />
                                        Approve
                                      </Button>
                                      <Button
                                        size="sm"
                                        className="bg-red-600 text-white hover:bg-red-700"
                                      >
                                        <X className="w-4 h-4 mr-1" />
                                        Reject
                                      </Button>
                                    </>
                                  )}
                                  {entry.paymentStatus === 'unpaid' && (
                                    <Button
                                      size="sm"
                                      className="bg-blue-600 text-white hover:bg-blue-700"
                                    >
                                      <DollarSign className="w-4 h-4 mr-1" />
                                      Mark Paid
                                    </Button>
                                  )}
                                  {entry.lemonsqueezyCustomerId && (
                                    <Button
                                      size="sm"
                                      variant="secondary"
                                      onClick={() => {
                                        navigator.clipboard.writeText(
                                          entry.lemonsqueezyCustomerId || ''
                                        );
                                      }}
                                    >
                                      <Copy className="w-4 h-4 mr-1" />
                                      Copy LS ID
                                    </Button>
                                  )}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Instructions */}
        <Card className="bg-blue-50 border-blue-200 mt-8">
          <CardContent className="p-6">
            <h3 className="font-semibold text-blue-900 mb-3">How It Works</h3>
            <ol className="text-sm text-blue-800 space-y-2 list-decimal list-inside">
              <li>Users submit email on waitlist → status: <Badge className="bg-yellow-100 text-yellow-900 text-xs">pending</Badge></li>
              <li>You approve them → moves them up queue, status: <Badge className="bg-blue-100 text-blue-900 text-xs">approved</Badge></li>
              <li>They pay via Lemonsqueezy link → mark as paid, <Badge className="bg-green-100 text-green-900 text-xs">paid</Badge></li>
              <li>Paid users automatically get access to the app</li>
              <li>Rejected users are removed from queue</li>
            </ol>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
